import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { DateInput } from "@/components/ui/date-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Calendar, Loader2, RotateCw } from "lucide-react";
import { CierreDiarioListado } from "./CierreDiarioListado";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserData } from "@/components/LoginPage";
import { cajaBancosAPI, opcionesAPI, type Sucursal } from "@/lib/api";
import { sessionIsElevated } from "@/lib/modulePermissions";
import {
  assignedPuntosVentaFromUser,
  pickDefaultPuntoVenta,
} from "@/lib/pvAsignado";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  formatArgentineMoneyPreview,
  formatArgentineMoneyTyping,
  toMoneyNumber,
} from "@/lib/parseArgentineDecimal";
import { formatDateOnlyEsAR, toNum } from "@/lib/utils";
import { roundToPeso } from "@/lib/roundToPeso";
import {
  cierreFechaYmd,
  nextCierreOpenYmd,
  sucursalIdOfCierre,
} from "./cierreFechaSiguiente";
import { cierreFinalizarNecesitaPassword } from "@/lib/cierreFinalizarPassword";

/** Mismo gate que los botones Reiniciar datos/stock (abs-dev). */
const demoResetFlag = import.meta.env.VITE_SHOW_DEMO_RESET_ACTIONS;
const showDemoFechaPicker = demoResetFlag === "true" || demoResetFlag === "1";

const toLocalYmd = (d: Date) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

function sucursalEsCasaCentral(s?: Pick<Sucursal, "esCasaCentral" | "es_casa_central"> | null): boolean {
  return Boolean(s?.esCasaCentral ?? s?.es_casa_central);
}

interface CierreDiarioSectionProps {
  user: UserData;
  cierres?: any[];
  onCierresUpdate?: (cierres: any[]) => void;
  movimientos?: any[];
  isActive?: boolean;
  isCurrentTab?: boolean;
  isReadOnly?: boolean;
  onSensitiveStateChange?: (isSensitive: boolean) => void;
}

export function CierreDiarioSection({
  user,
  cierres = [],
  onCierresUpdate,
  isActive = true,
  isCurrentTab = true,
  isReadOnly = false,
  onSensitiveStateChange
}: CierreDiarioSectionProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showFinalizarDialog, setShowFinalizarDialog] = useState(false);
  const [cierreToFinalize, setCierreToFinalize] = useState<string | null>(null);
  const [finalizarNeedsPassword, setFinalizarNeedsPassword] = useState(false);
  const [finalizarPassword, setFinalizarPassword] = useState("");
  /** Display ARS (máscara typing); parsear con toMoneyNumber al guardar. */
  const [saldoInicial, setSaldoInicial] = useState("");
  const [saldoInicialEditable, setSaldoInicialEditable] = useState(true);
  const [fechaCierre, setFechaCierre] = useState(() => toLocalYmd(new Date()));
  const [isCerrando, setIsCerrando] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dirtyDetailIds, setDirtyDetailIds] = useState<Set<number>>(new Set());
  const [discardEditsToken, setDiscardEditsToken] = useState(0);
  const [sucursales, setSucursales] = useState<Sucursal[]>([]);
  const [selectedSucursalId, setSelectedSucursalId] = useState<string>("");
  const hasDirtyDetails = dirtyDetailIds.size > 0;
  const hasSensitiveState = showConfirmDialog || showFinalizarDialog || isCerrando || isRefreshing || hasDirtyDetails;

  const todayYmd = toLocalYmd(new Date());
  const canCierreSucursalAjena = sessionIsElevated(user);

  React.useEffect(() => {
    let cancelled = false;
    opcionesAPI.listSucursales().then((rows) => {
      if (!cancelled) setSucursales(rows.filter((s) => s.activo !== false));
    }).catch(() => {
      if (!cancelled) setSucursales([]);
    });
    return () => { cancelled = true; };
  }, []);

  const selectableSucursales = React.useMemo(() => {
    const active = sucursales.filter((s) => s.activo !== false && s.id);
    if (canCierreSucursalAjena) return active;
    const assigned = assignedPuntosVentaFromUser(user);
    const ids = new Set(assigned.filter((pv) => pv.activo).map((pv) => pv.sucursal));
    return active.filter((s) => ids.has(Number(s.id)));
  }, [sucursales, canCierreSucursalAjena, user]);

  React.useEffect(() => {
    if (selectedSucursalId) {
      const still = selectableSucursales.some((s) => String(s.id) === selectedSucursalId);
      if (still) return;
    }
    const assigned = assignedPuntosVentaFromUser(user);
    const defPv = pickDefaultPuntoVenta(user, assigned);
    const defSuc = defPv?.sucursal ? String(defPv.sucursal) : "";
    if (defSuc && selectableSucursales.some((s) => String(s.id) === defSuc)) {
      setSelectedSucursalId(defSuc);
      return;
    }
    if (selectableSucursales.length > 0) {
      const cc = selectableSucursales.find((s) => sucursalEsCasaCentral(s));
      setSelectedSucursalId(String((cc ?? selectableSucursales[0]).id));
    }
  }, [selectableSucursales, user, selectedSucursalId]);

  const operatorSucursalIds = React.useMemo(() => {
    if (canCierreSucursalAjena) return null;
    return new Set(
      assignedPuntosVentaFromUser(user)
        .filter((pv) => pv.activo)
        .map((pv) => Number(pv.sucursal))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
  }, [canCierreSucursalAjena, user]);

  const scopedCierres = React.useMemo(() => {
    if (!operatorSucursalIds) return cierres;
    return cierres.filter((c) => {
      const sid = sucursalIdOfCierre(c);
      return sid != null && operatorSucursalIds.has(sid);
    });
  }, [cierres, operatorSucursalIds]);

  const listadoSucursales = canCierreSucursalAjena ? sucursales : selectableSucursales;

  const selectedSucursal = selectableSucursales.find((s) => String(s.id) === selectedSucursalId) ?? null;
  const selectedIsCasaCentral = sucursalEsCasaCentral(selectedSucursal);
  const selectedSucursalPk = selectedSucursal?.id ? Number(selectedSucursal.id) : null;
  const nextOpenYmd = React.useMemo(
    () => nextCierreOpenYmd(scopedCierres, selectedSucursalPk, todayYmd),
    [scopedCierres, selectedSucursalPk, todayYmd],
  );
  const targetYmd = showDemoFechaPicker ? fechaCierre : nextOpenYmd;

  /** F11/Req11: arrastre = SI + efectivo neto del cierre más reciente anterior de ESA sucursal. */
  const resolveSaldoInicialSugerido = React.useCallback(
    (ymd: string, sucursalId: number | null) => {
      const anteriores = scopedCierres
        .map((c) => ({
          ymd: cierreFechaYmd(c.fecha),
          sucursalId: sucursalIdOfCierre(c),
          cierre: c,
        }))
        .filter((x) => x.ymd && x.ymd < ymd && (sucursalId == null || x.sucursalId === sucursalId))
        .sort((a, b) => (a.ymd < b.ymd ? 1 : a.ymd > b.ymd ? -1 : 0));
      const prev = anteriores[0]?.cierre;
      if (!prev) {
        return { value: "", editable: true };
      }
      const si = toNum(prev.saldoInicial ?? prev.saldo_inicial);
      const efIng = toNum(prev.efectivoIngresos ?? prev.efectivo_ingresos);
      const efGas = toNum(prev.efectivoGastos ?? prev.efectivo_gastos);
      const arrastre = si + efIng - efGas;
      return { value: formatArgentineMoneyPreview(arrastre), editable: false };
    },
    [scopedCierres],
  );

  const handleDetailDirtyStateChange = React.useCallback((detalleId: number, dirty: boolean) => {
    setDirtyDetailIds((current) => {
      const next = new Set(current);
      if (dirty) next.add(detalleId);
      else next.delete(detalleId);
      return next;
    });
  }, []);

  React.useEffect(() => {
    onSensitiveStateChange?.(hasSensitiveState);
    return () => onSensitiveStateChange?.(false);
  }, [hasSensitiveState, onSensitiveStateChange]);

  // Auto-refresh every 30 minutes
  React.useEffect(() => {
    if (!isActive || !isCurrentTab || hasSensitiveState) return;

    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      if (hasSensitiveState) return;
      const openClosure = scopedCierres.find(
        (c) =>
          !c.cerrado &&
          cierreFechaYmd(c.fecha) === todayYmd &&
          (selectedSucursalPk == null || sucursalIdOfCierre(c) === selectedSucursalPk),
      );

      if (openClosure) {
        handleRefresh(openClosure);
      }
    }, 30 * 60 * 1000); // 30 minutes

    return () => clearInterval(interval);
  }, [scopedCierres, isActive, isCurrentTab, hasSensitiveState, todayYmd]);

  /** Hay cierre abierto para esa fecha y sucursal (YMD local, sin parse UTC). */
  const hasOpenClosureForDate = (ymd: string, sucursalId?: number | null) =>
    scopedCierres.some(
      (cierre) =>
        !cierre.cerrado &&
        cierreFechaYmd(cierre.fecha) === ymd &&
        (sucursalId == null || sucursalIdOfCierre(cierre) === sucursalId),
    );

  const hasClosedClosureForDate = (ymd: string, sucursalId?: number | null) =>
    scopedCierres.some(
      (cierre) =>
        Boolean(cierre.cerrado) &&
        cierreFechaYmd(cierre.fecha) === ymd &&
        (sucursalId == null || sucursalIdOfCierre(cierre) === sucursalId),
    );

  const hasOpenForTarget = hasOpenClosureForDate(targetYmd, selectedSucursalPk);

  const handleRefresh = async (existingClosure?: any) => {
    if (hasDirtyDetails) {
      const discard = window.confirm(
        "Hay observaciones sin guardar. ¿Descartarlas y actualizar los datos?"
      );
      if (!discard) return;
      setDiscardEditsToken((token) => token + 1);
      setDirtyDetailIds(new Set());
    }

    let saldo = 0;
    let fechaRefresh = todayYmd;
    const sucursalRefresh =
      existingClosure != null
        ? sucursalIdOfCierre(existingClosure)
        : selectedSucursalPk;
    if (existingClosure) {
      saldo = existingClosure.saldoInicial || existingClosure.saldo_inicial || 0;
      fechaRefresh = cierreFechaYmd(existingClosure.fecha) || todayYmd;
    } else {
      const open =
        scopedCierres.find(
          (c) =>
            !c.cerrado &&
            cierreFechaYmd(c.fecha) === targetYmd &&
            (sucursalRefresh == null || sucursalIdOfCierre(c) === sucursalRefresh),
        ) ||
        scopedCierres.find(
          (c) =>
            !c.cerrado &&
            cierreFechaYmd(c.fecha) === todayYmd &&
            (sucursalRefresh == null || sucursalIdOfCierre(c) === sucursalRefresh),
        );
      if (open) {
        saldo = open.saldoInicial || open.saldo_inicial || 0;
        fechaRefresh = cierreFechaYmd(open.fecha) || todayYmd;
      }
    }

    if (!sucursalRefresh) {
      toast.error("Elegí la sucursal del cierre.");
      return;
    }

    try {
      setIsRefreshing(true);
      const payload: { saldo_inicial: number; sucursal: number; fecha?: string } = {
        saldo_inicial: roundToPeso(saldo),
        sucursal: sucursalRefresh,
      };
      // Siempre enviar la fecha del cierre que se refresca (BE regenera ese día
      // si ya existe; sin esto, con hoy cerrado el refresh de mañana falla).
      if (fechaRefresh) {
        payload.fecha = fechaRefresh;
      }
      await cajaBancosAPI.generarCierre(payload);
      toast.success("Datos actualizados correctamente");
      if (onCierresUpdate) onCierresUpdate([]);
    } catch (error: any) {
      console.error("Error updating cierre:", error);
      toast.error(
        error?.message || error?.error || "Error al actualizar el cierre diario",
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCrearCierre = () => {
    if (!selectedSucursalPk) {
      toast.error("Elegí la sucursal del cierre.");
      return;
    }
    // En demo, el diálogo deja cambiar la fecha; validamos al confirmar.
    if (!showDemoFechaPicker) {
      if (hasClosedClosureForDate(nextOpenYmd, selectedSucursalPk)) {
        toast.error(
          "Ya existe un cierre diario FINALIZADO de esa sucursal para esa fecha. No se puede modificar.",
        );
        return;
      }
      if (hasOpenClosureForDate(nextOpenYmd, selectedSucursalPk)) {
        toast.error("Ya hay un cierre abierto de esa sucursal para esa fecha. Usá «Actualizar Datos».");
        return;
      }
    }
    const sugerido = resolveSaldoInicialSugerido(
      showDemoFechaPicker ? fechaCierre : nextOpenYmd,
      selectedSucursalPk,
    );
    setSaldoInicial(sugerido.value);
    setSaldoInicialEditable(sugerido.editable);
    setShowConfirmDialog(true);
  };

  // Demo: al cambiar la fecha del cierre, recalcular arrastre de saldo inicial.
  React.useEffect(() => {
    if (!showConfirmDialog || !showDemoFechaPicker) return;
    const sugerido = resolveSaldoInicialSugerido(fechaCierre, selectedSucursalPk);
    setSaldoInicial(sugerido.value);
    setSaldoInicialEditable(sugerido.editable);
  }, [fechaCierre, showConfirmDialog, showDemoFechaPicker, resolveSaldoInicialSugerido, selectedSucursalPk]);

  const handleFinalizarCierre = (cierreId: string) => {
    const closure = scopedCierres.find(c => c.id?.toString() === cierreId || c.fecha === cierreId);
    if (!closure) return;

    if (closure.cerrado) {
      toast.error("Este cierre ya está finalizado");
      return;
    }

    setCierreToFinalize(cierreId);
    setFinalizarNeedsPassword(cierreFinalizarNecesitaPassword(closure.fecha, todayYmd));
    setFinalizarPassword("");
    setShowFinalizarDialog(true);
  };

  const handleFinalizarDialogOpenChange = (open: boolean) => {
    setShowFinalizarDialog(open);
    if (!open) setFinalizarPassword("");
  };

  const handleConfirmFinalizar = async (e?: React.MouseEvent) => {
    if (!cierreToFinalize) return;
    if (finalizarNeedsPassword) {
      e?.preventDefault();
      if (!finalizarPassword.trim()) {
        toast.error("Debe ingresar su contraseña");
        return;
      }
    }

    try {
      setIsCerrando(true);
      // Assuming closure ID or FECHA is used for closure identification
      await cajaBancosAPI.cerrarCierre(cierreToFinalize, finalizarPassword || undefined);
      toast.success("Cierre diario finalizado. No se podrán realizar más modificaciones.");
      setShowFinalizarDialog(false);
      setCierreToFinalize(null);
      setFinalizarPassword("");
      if (onCierresUpdate) onCierresUpdate([]); // Trigger refresh
    } catch (error) {
      console.error("Error finalizando cierre:", error);
      const apiError = error as { error?: string; message?: string; status?: number };
      toast.error(
        (apiError?.status === 403 && apiError?.message) ||
          apiError?.error ||
          apiError?.message ||
          "Error al finalizar el cierre diario"
      );
    } finally {
      setIsCerrando(false);
    }
  };

  const handleConfirmCrearCierre = async () => {
    const ymd = showDemoFechaPicker ? fechaCierre : nextOpenYmd;
    if (!ymd) {
      toast.error("La fecha es requerida");
      return;
    }
    if (!selectedSucursalPk) {
      toast.error("Elegí la sucursal del cierre.");
      return;
    }
    const sucNombre = selectedSucursal?.nombre || "la sucursal";
    if (hasClosedClosureForDate(ymd, selectedSucursalPk)) {
      toast.error(
        `Ya existe un cierre diario FINALIZADO de ${sucNombre} para ${formatDateOnlyEsAR(ymd)}. No se puede modificar.`,
      );
      return;
    }
    if (hasOpenClosureForDate(ymd, selectedSucursalPk)) {
      toast.error(
        `Ya hay un cierre abierto de ${sucNombre} para ${formatDateOnlyEsAR(ymd)}. Usá «Actualizar Datos».`,
      );
      return;
    }

    try {
      const payload: { saldo_inicial: number; sucursal: number; fecha?: string } = {
        saldo_inicial: roundToPeso(toMoneyNumber(saldoInicial) ?? 0),
        sucursal: selectedSucursalPk,
        fecha: ymd,
      };
      await cajaBancosAPI.generarCierre(payload);
      toast.success(
        `Cierre diario generado (${sucNombre} · ${formatDateOnlyEsAR(ymd)})`,
      );
      setShowConfirmDialog(false);
      setSaldoInicial("");
      if (onCierresUpdate) onCierresUpdate([]); // Trigger refresh
    } catch (error) {
      console.error("Error generando cierre:", error);
      const apiError = error as { error?: string; message?: string };
      toast.error(
        apiError?.error ||
          apiError?.message ||
          "Error al generar el cierre diario",
      );
    }
  };

  // El botón no se deshabilita porque haya un abierto en otra sucursal.
  const createDisabled = false;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-md">
            <Calendar className="h-5 w-5 text-white" />
          </div>
          <h3 className="text-2xl font-bold">Cierre Diario</h3>
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2">
          {showDemoFechaPicker && (
            <div className="flex items-center gap-1.5">
              <Label
                htmlFor="fechaCierreToolbar"
                className="text-xs text-amber-800 whitespace-nowrap"
              >
                Fecha (pruebas)
              </Label>
              <DateInput
                id="fechaCierreToolbar"
                value={fechaCierre}
                onChange={(ymd) => setFechaCierre(ymd)}
                className="w-[10.5rem]"
              />
            </div>
          )}
          {(hasOpenForTarget) && (
            <Button
              onClick={() => {
                const open = scopedCierres.find(
                  (c) =>
                    !c.cerrado &&
                    cierreFechaYmd(c.fecha) === targetYmd &&
                    (selectedSucursalPk == null || sucursalIdOfCierre(c) === selectedSucursalPk),
                );
                handleRefresh(open || null);
              }}
              variant="outline"
              size="sm"
              disabled={isRefreshing}
              className="flex items-center gap-1"
            >
              <RotateCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-xs">Actualizar Datos</span>
            </Button>
          )}
          {!isReadOnly && (
          <Button
            onClick={handleCrearCierre}
            disabled={createDisabled || selectableSucursales.length === 0}
            variant="default"
            size="sm"
            className="flex items-center gap-1"
          >
            <Plus className="h-4 w-4" />
            <span className="text-xs">Cierre Diario</span>
          </Button>
          )}
        </div>
      </div>

      <CierreDiarioListado
        cierres={scopedCierres}
        sucursales={listadoSucursales}
        onFinalizarCierre={handleFinalizarCierre}
        onRefresh={handleRefresh}
        onCierreSilentRefresh={() => onCierresUpdate?.([])}
        isReadOnly={isReadOnly}
        discardEditsToken={discardEditsToken}
        onDirtyStateChange={handleDetailDirtyStateChange}
      />

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>Crear Cierre Diario</AlertDialogTitle>
            <AlertDialogDescription>
              Se va a generar el cierre de{" "}
              <strong>{selectedSucursal?.nombre || "la sucursal"}</strong>
              {selectedIsCasaCentral ? " (Casa central)" : ""} para{" "}
              {formatDateOnlyEsAR(showDemoFechaPicker ? fechaCierre : nextOpenYmd)}.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="sucursalCierre">Sucursal</Label>
              <Select
                value={selectedSucursalId}
                onValueChange={(v) => {
                  setSelectedSucursalId(v);
                  const suc = selectableSucursales.find((s) => String(s.id) === v);
                  const sid = suc?.id ? Number(suc.id) : null;
                  const sugerido = resolveSaldoInicialSugerido(
                    showDemoFechaPicker ? fechaCierre : nextOpenYmd,
                    sid,
                  );
                  setSaldoInicial(sugerido.value);
                  setSaldoInicialEditable(sugerido.editable);
                }}
              >
                <SelectTrigger id="sucursalCierre">
                  <SelectValue placeholder="Seleccionar sucursal" />
                </SelectTrigger>
                <SelectContent>
                  {selectableSucursales.map((suc) => (
                    <SelectItem key={suc.id} value={String(suc.id)}>
                      <span className="inline-flex items-center gap-2">
                        {suc.nombre}
                        {sucursalEsCasaCentral(suc) ? (
                          <Badge variant="secondary" className="text-[10px]">
                            Casa central
                          </Badge>
                        ) : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {selectedIsCasaCentral
                  ? "Casa central: ventas y NC de su punto de venta, más caja, fondos, recibos y pagos a proveedores."
                  : "Esta sucursal: solo ventas y notas de crédito de su punto de venta."}
              </p>
            </div>
            {showDemoFechaPicker && (
              <div className="space-y-2 rounded-md border border-amber-200 bg-amber-50/80 p-3">
                <Label htmlFor="fechaCierreDemo" className="text-sm font-medium text-amber-900">
                  Fecha del cierre (solo pruebas abs-dev)
                </Label>
                <DateInput
                  id="fechaCierreDemo"
                  value={fechaCierre}
                  onChange={(ymd) => setFechaCierre(ymd)}
                  popoverModal
                />
                <p className="text-xs text-amber-800">
                  Permite abrir cierres de fechas no secuenciales. En uso normal
                  ya se pueden tener varios abiertos (uno por día) sin finalizar
                  el anterior. No aparece en preview/prod.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="saldoInicial" className="text-sm font-medium">
                Saldo Inicial en Caja
              </label>
              <Input
                id="saldoInicial"
                type="text"
                inputMode="decimal"
                value={saldoInicial}
                onChange={(e) =>
                  setSaldoInicial(
                    saldoInicialEditable
                      ? formatArgentineMoneyTyping(e.target.value)
                      : e.target.value,
                  )
                }
                onBlur={() => {
                  if (!saldoInicialEditable) return;
                  const rounded = roundToPeso(toMoneyNumber(saldoInicial) ?? 0);
                  setSaldoInicial(formatArgentineMoneyPreview(rounded));
                }}
                placeholder="0,00"
                readOnly={!saldoInicialEditable}
                disabled={!saldoInicialEditable}
              />
              <p className="text-xs text-muted-foreground">
                {saldoInicialEditable
                  ? "Primer cierre: ingresá el efectivo disponible al inicio del día."
                  : "Arrastre automático: saldo inicial + efectivo neto del cierre anterior (solo editable en el primer cierre)."}
              </p>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmCrearCierre}>
              Crear Cierre
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showFinalizarDialog} onOpenChange={handleFinalizarDialogOpenChange}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Finalizar Cierre Diario</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro que desea finalizar este cierre diario?
              <br /><br />
              Una vez finalizado, no se podrán realizar modificaciones.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {finalizarNeedsPassword ? (
            <div className="space-y-2">
              <Label htmlFor="cierre-finalizar-password">Contraseña</Label>
              <Input
                id="cierre-finalizar-password"
                type="password"
                value={finalizarPassword}
                onChange={(e) => setFinalizarPassword(e.target.value)}
                placeholder="Ingrese su contraseña"
                autoComplete="current-password"
                disabled={isCerrando}
              />
              <p className="text-xs text-muted-foreground">
                Está finalizando el cierre del día de hoy. Las ventas posteriores pasarán al día
                siguiente. Confirme con la contraseña de su sesión.
              </p>
            </div>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setCierreToFinalize(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmFinalizar}
              disabled={isCerrando || (finalizarNeedsPassword && !finalizarPassword.trim())}
              className="bg-primary text-primary-foreground"
            >
              {isCerrando ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Finalizar Cierre
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div >
  );
}
